import { QueryClient } from 'react-query'
import * as Actions from 'redux/actions'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import store from 'redux/store'
import { ArticleClient, TimelineEventFilter } from 'api/clients/ArticleClient'
import { ArticleFlat, ArticleMention } from 'types'
import { FavoritableType } from 'api/clients/FavoriteClient'
import { favoritesSet } from 'redux/actions'
import { TimelineEvent } from 'screens/Activity/Types'
import { updateTimelineEvent } from 'redux/actions/timeline'
import { articlesKeys } from '../../hooks/queries/articles/articlesKeys'
import produce from 'immer'

export const ArticleService = {
  getArticle: async (
    id: string,
    options: {
      withExtraSteps?: boolean
      withAccessMap?: boolean
      withAncestors?: boolean
      enteredPassword?: string | null
      handbookKey?: string
      withArticleView?: boolean
    } = {},
  ) => {
    const response = await ArticleClient.getArticle(id, {
      withExtraSteps: options.withExtraSteps,
      withAccessMap: options.withAccessMap,
      withAncestors: !!options.withAncestors,
      enteredPassword: options.enteredPassword,
      handbookKey: options.handbookKey,
      withArticleView: !!options.withArticleView,
    })

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      const {
        data: {
          article: { article_reaction },
        },
      } = response

      if (article_reaction) {
        store.dispatch(Actions.addReaction(article_reaction))
      }
      store.dispatch(Actions.articleMetaSet(response.data.article))
    }

    return response
  },
  getDocMentions: async (articleId: string) => {
    return sendToApi<{ mentions: ArticleMention[] }>(`/articles/${articleId}/mentions`)
  },

  getTitlesFromKeys: async (queryClient: QueryClient, keys: string[]) => {
    return queryClient.fetchQuery(['titles_from_keys', keys], async () => {
      const res = await ArticleClient.fetchSmartLinkTitles(keys)
      if (res.type !== SUCCESS_RESPONSE_TYPE) return null
      return res.data
    })
  },

  updateArticle: async (
    id: string,
    body: {},
    clientUuid: undefined | string = undefined,
    queryClient?: QueryClient,
  ): Promise<any> => {
    const response = await ArticleClient.updateArticle(id, body, clientUuid)

    if (response.type !== 'success') return response

    const favorites = store.getState()['favorites']
    store.dispatch(
      favoritesSet(
        favorites.map((favorite) =>
          favorite.favoritable_type === FavoritableType.Article && favorite.favoritable_id === id
            ? {
                ...favorite,
                favoritable_emoji: response.data.article.header_emoji,
                favoritable_title: response.data.article.title,
              }
            : favorite,
        ),
      ),
    )

    store.dispatch(Actions.articleMetaUpsert(response.data.article))
    queryClient?.setQueriesData<{ article: ArticleFlat } | undefined>([articlesKeys.prefix, 'detail', id], (old) => {
      return produce(old, (draft) => {
        if (!draft) {
          return
        }

        draft.article = { ...draft.article, ...response.data.article }
      })
    })

    return response
  },

  fetchRecentlyReadArticles: async (articleId: string) => {
    const response = await ArticleClient.getRecentlyReadArticles(articleId)

    if (response.type === 'success') {
      store.dispatch(Actions.updateSmartLinkHelper(response.data))
    }

    return response
  },

  fetchTimelineEvents: (
    articleKey: string,
    userId: string | undefined,
    withChangesOnly: boolean,
    queryClient: QueryClient = new QueryClient(),
    layerId?: string,
    types?: TimelineEventFilter[],
    timestamp?: string,
  ) => {
    return queryClient.fetchQuery(
      ['timeline', articleKey, layerId, { timestamp, types }],
      () => ArticleClient.getTimelineEvents(articleKey, timestamp, userId, withChangesOnly, layerId, types),
      { staleTime: 5000 },
    )
  },

  updateEvent: async (event: TimelineEvent, attributes: { name: string | null }) => {
    const res = await ArticleClient.updateEvent(event.article.key, event.id, attributes)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      store.dispatch(updateTimelineEvent({ ...event, name: attributes.name }))
    }

    return res
  },
}
